import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * ThresholdNode
 * -------------
 * Converts canvas to black/white at a controllable cutoff.
 * Great for creating high-contrast masks from text or patterns.
 */
export class ThresholdNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Threshold";

    this.properties = {
      cutoff: 0.5,
      invert: false,
    };

    this.addInput("Canvas", "canvas");
    this.addOutput("Canvas", "canvas");

    addDrivableSlider(this, "Cutoff", "cutoff", 0.5, 0, 1, 0.01);

    finalizeDrivableSliders(this, 1);

    this.addWidget("toggle", "Invert", false, (val) => {
      this.properties.invert = val;
    });

    this._outCanvas = document.createElement("canvas");
    this._outCtx = this._outCanvas.getContext("2d");

    this._tmpCanvas = document.createElement("canvas");
    this._tmpCtx = this._tmpCanvas.getContext("2d");

    this.size = [200, 80];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onExecute() {
    syncDrivableSliders(this);

    const input = this.getInputData(0);
    if (!input) { this.setOutputData(0, null); return; }

    const w = input.width;
    const h = input.height;
    if (this._outCanvas.width !== w) this._outCanvas.width = w;
    if (this._outCanvas.height !== h) this._outCanvas.height = h;

    const ctx = this._outCtx;

    // Downscale for the per-pixel threshold work
    const MAX_DIM = 800;
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    const sw = Math.round(w * scale);
    const sh = Math.round(h * scale);

    if (this._tmpCanvas.width !== sw) this._tmpCanvas.width = sw;
    if (this._tmpCanvas.height !== sh) this._tmpCanvas.height = sh;

    const tmpCtx = this._tmpCtx;
    tmpCtx.clearRect(0, 0, sw, sh);
    tmpCtx.drawImage(input, 0, 0, sw, sh);

    const imageData = tmpCtx.getImageData(0, 0, sw, sh);
    const data = imageData.data;
    const cutoff = this.properties.cutoff * 255;
    const inv = this.properties.invert;

    for (let i = 0; i < data.length; i += 4) {
      const brightness = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      let val = brightness >= cutoff ? 255 : 0;
      if (inv) val = 255 - val;
      data[i] = val;
      data[i + 1] = val;
      data[i + 2] = val;
    }

    tmpCtx.putImageData(imageData, 0, 0);

    // Draw thresholded result back up to full resolution
    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this._tmpCanvas, 0, 0, w, h);

    this.setOutputData(0, this._outCanvas);
  }
}

LiteGraph.registerNodeType("effects/Threshold", ThresholdNode);
